import {faCircleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Alert} from 'antd';
import {useMemo, useState} from 'react';

import {Locale} from '../../../../provider/locale/locale';
import {SkippedCompanyType, VideoSkipReasonEnum} from '../../../../service/company/company-type';

import {
    getVideoSkipReason,
    VIDEO_SKIP_REASON_GROUP_TITLE,
    VIDEO_SKIP_REASON_MESSAGE,
    VIDEO_SKIP_REASONS_ORDER,
} from './skipped-companies-alert-const';
import {SkippedCompaniesList} from './skipped-companies-list';
import * as styles from './skipped-companies-alert.scss';

type PropsType = {
    skippedCompanies: Array<SkippedCompanyType>;
    /** Total number of companies the update was submitted for — tells "all of them" from "some of them" apart. */
    companiesCount: number;
};

type SkippedGroupType = {
    reason: VideoSkipReasonEnum;
    companies: Array<SkippedCompanyType>;
};

export function SkippedCompaniesAlert(props: PropsType): JSX.Element | null {
    const {skippedCompanies, companiesCount} = props;
    // The field unmounts the alert once no video is left, so the next upload shows it again.
    const [isClosed, setIsClosed] = useState<boolean>(false);

    const groups = useMemo<Array<SkippedGroupType>>(
        () =>
            VIDEO_SKIP_REASONS_ORDER.map((reason) => ({
                reason,
                companies: skippedCompanies.filter((company) => getVideoSkipReason(company.reason) === reason),
            })).filter((group) => group.companies.length > 0),
        [skippedCompanies]
    );

    const [firstGroup] = groups;

    if (isClosed || !firstGroup) {
        return null;
    }

    // The backend reports no total, so "all of them" is told apart by comparing with the selection.
    const isEveryCompanySkipped = skippedCompanies.length >= companiesCount;

    function renderContent(singleGroup: SkippedGroupType): {message: JSX.Element; description: JSX.Element | null} {
        if (groups.length > 1) {
            return {
                message: (
                    <Locale
                        stringKey={
                            isEveryCompanySkipped
                                ? 'BULK_EDIT_COMPANIES__VIDEO_SKIPPED__MIXED__ALL'
                                : 'BULK_EDIT_COMPANIES__VIDEO_SKIPPED__MIXED__PART'
                        }
                    />
                ),
                description: (
                    <>
                        {groups.map((group) => (
                            <div key={group.reason}>
                                <Text bolder stringKey={VIDEO_SKIP_REASON_GROUP_TITLE[group.reason]} />
                                <SkippedCompaniesList companies={group.companies} />
                            </div>
                        ))}
                    </>
                ),
            };
        }

        const reasonMessage = VIDEO_SKIP_REASON_MESSAGE[singleGroup.reason];

        if (isEveryCompanySkipped) {
            // The list would just repeat the selection, so the reason alone is enough.
            return {message: <Locale stringKey={reasonMessage.all} />, description: null};
        }

        return {
            message: <Locale stringKey={reasonMessage.part} />,
            description: (
                <div>
                    <Text stringKey="BULK_EDIT_COMPANIES__VIDEO_SKIPPED__LIST_TITLE" />
                    <SkippedCompaniesList companies={singleGroup.companies} />
                </div>
            ),
        };
    }

    const {message, description} = renderContent(firstGroup);

    // Everything goes into `message`: given a `description`, antd switches to its large variant
    // (bigger icon, font and padding), while the mockup uses the compact one.

    return (
        <div className={styles.SkippedCompaniesAlert}>
            <Alert
                closable
                icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                message={
                    <div className={styles.SkippedCompaniesAlert_content}>
                        {message}
                        {description}
                    </div>
                }
                onClose={() => setIsClosed(true)}
                showIcon
                type="warning"
            />
        </div>
    );
}
